import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

/**
 *
 * @returns [search, setSearch]
 * - search: the current search query
 * - setSearch: a function to set the search query
 *
 * The search query is synced between the URL and the local state.
 * The function `setSearch` updates both the local state and the URL.
 *
 */
export const useSearchQuery = (): [
  string,
  (search: string | undefined) => void,
] => {
  const location = useLocation();
  const history = useHistory();

  // Track if we're updating from URL to prevent circular updates
  const isUpdatingFromUrlRef = useRef(false);
  const lastUrlSearchRef = useRef<string>('');

  const [search, setSearchState] = useState<string>(() => {
    const params = new URLSearchParams(location.search);
    const initialSearch = params.get('search') ?? '';
    lastUrlSearchRef.current = initialSearch;
    return initialSearch;
  });

  // Sync URL -> state when URL changes (but not when we're the ones updating it)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlSearch = params.get('search') ?? '';

    // Only update state if URL changed and we didn't trigger the change
    if (
      !isUpdatingFromUrlRef.current &&
      urlSearch !== lastUrlSearchRef.current
    ) {
      lastUrlSearchRef.current = urlSearch;
      setSearchState(urlSearch);
    } else if (isUpdatingFromUrlRef.current) {
      // Reset the flag after we've processed our own update
      isUpdatingFromUrlRef.current = false;
      lastUrlSearchRef.current = urlSearch;
    } else {
      // Keep ref in sync even if we don't update state
      lastUrlSearchRef.current = urlSearch;
    }
  }, [location.search]);

  // Sync state -> URL when state changes (but not when we're updating from URL)
  const setSearch = (_newSearch: string | undefined) => {
    const newSearch = _newSearch ?? '';
    setSearchState(newSearch);

    const params = new URLSearchParams(location.search);
    if (newSearch) {
      params.set('search', newSearch);
    } else {
      params.delete('search');
    }

    const newSearchString = params.toString();
    const newUrl = newSearchString
      ? `${location.pathname}?${newSearchString}`
      : location.pathname;

    // Mark that we're updating the URL so we don't trigger the URL -> state sync
    isUpdatingFromUrlRef.current = true;
    lastUrlSearchRef.current = newSearch;
    history.push(newUrl);
  };

  return [search, setSearch];
};

/**
 *
 * @param contextSearch - the search query from the context
 * @returns [search, setSearch]
 * - search: the current search query
 * - setSearch: a function to set the search query
 *
 * This hook is used for local input fields that need to be synced with the context.
 * This hook does not update the context.
 *
 */
export const useSyncedLocalSearchInput = (
  contextSearch: string | undefined
): [string, Dispatch<SetStateAction<string>>] => {
  const [search, setSearch] = useState(contextSearch ?? '');
  const lastContextSearchRef = useRef(contextSearch);

  useEffect(() => {
    if (lastContextSearchRef.current !== contextSearch) {
      lastContextSearchRef.current = contextSearch;
      setSearch(contextSearch ?? '');
    }
  }, [contextSearch]);

  return [search, setSearch];
};
